import { api, acct } from "./client";
import type { ListQuery, Page } from "./types";
import type { Activity, Availability } from "./queues";

/**
 * Agents. An agent is a user of the account with a `call_center` object on
 * their user document, so this collection has no document of its own: the
 * settings that belong to the person are edited through `users`, and this
 * module is about what the agent is doing.
 *
 * Hand-typed from `applications/crossbar/doc/agents.md` and the
 * `callcenter_agent.json` schema.
 *
 * Availability and activity are independent and neither is per queue:
 * availability is what the agent, a supervisor or an automatic rule chose
 * and is persisted; activity is derived from the channels the user's
 * phones have and cannot be set.
 */

export type { Activity, Availability };

/** What made the last availability change. `ui` and `supervisor` are the two a PATCH may claim. */
export type StatusSource = "phone" | "ui" | "supervisor" | "no_answer" | "dnd" | "system";

export const AVAILABILITY_VALUES: Availability[] = ["available", "available_on_demand", "on_break", "logged_out"];

export const AVAILABILITY_LABEL: Record<Availability, string> = {
  logged_out: "Logged out",
  available: "Available",
  available_on_demand: "Available on demand",
  on_break: "On break",
};

export const AVAILABILITY_HELP: Record<Availability, string> = {
  logged_out: "Takes no queue calls.",
  available: "Offered queue callers whenever their phones are free.",
  available_on_demand: "Offered a caller only while someone is actually waiting.",
  on_break: "Stays logged in but is not offered callers.",
};

export const ACTIVITY_LABEL: Record<Activity, string> = {
  idle: "Idle",
  ringing: "Ringing",
  on_call: "On a call",
  wrapping_up: "Wrapping up",
};

export const SOURCE_LABEL: Record<StatusSource, string> = {
  phone: "a feature code",
  ui: "the console",
  supervisor: "a supervisor",
  no_answer: "the missed-call rule",
  dnd: "do not disturb",
  system: "the system",
};

/** One agent's tier in a queue, as `GET .../agents/{id}` reports it. */
export interface AgentTier {
  queue_id: string;
  queue_name?: string;
  level?: number;
  position?: number;
}

export interface AgentSummary {
  id: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  availability?: Availability;
  activity?: Activity;
  /** The queues this agent has a tier in: ids on the listing, tier objects on the single read. */
  queues?: Array<string | AgentTier>;
  /** When the availability last changed, in Gregorian seconds. */
  timestamp?: number;
  /**
   * `false` for a disabled user: never offered a caller, and refused any
   * availability change but a log out. A deleted user is not listed at all.
   */
  enabled?: boolean;
}

/** The single-agent read: the same entry with the tier in each queue. */
export interface AgentDetail extends AgentSummary {
  tiers?: AgentTier[];
}

export interface AgentStatus {
  agent_id?: string;
  availability?: Availability;
  activity?: Activity;
  source?: StatusSource;
  /** Gregorian seconds. */
  timestamp?: number;
  /** When wrap-up ends, in Gregorian seconds, while the activity is `wrapping_up`. */
  wrapup_ends?: number;
}

/** One row of the status log, newest first. */
export interface AgentLogEntry {
  availability?: Availability;
  previous?: Availability;
  source?: StatusSource;
  /** Gregorian seconds. */
  timestamp?: number;
}

/**
 * What the missed counter counts, in the words every screen that shows it
 * uses. The counter is reset by a log in and only an offer that rang out
 * while the agent was logged in and off break adds to it.
 */
/** What a disabled user is as an agent, in the words every screen that marks one uses. */
export const DISABLED_AGENT_NOTE = "This user is disabled: they are offered no calls and can only be logged out.";

export const MISSED_NOTE = "Since login; offers that rang out while on break or logged out are not counted";

/** The counters kept since the agent last logged in; the ones the strategies run on. */
export interface AgentStats {
  calls_answered?: number;
  talk_time_s?: number;
  missed?: number;
  /** Gregorian seconds. */
  last_call_end?: number;
}

export const agents = {
  list: (accountId: string, q?: ListQuery): Promise<Page<AgentSummary>> => api.list<AgentSummary>(acct(accountId, "agents"), { paginate: false, ...q }),
  get: (accountId: string, userId: string) => api.get<AgentDetail>(acct(accountId, "agents", userId)),
  status: (accountId: string, userId: string) => api.get<AgentStatus>(acct(accountId, "agents", userId, "status")),
  /**
   * Sets availability only; activity is derived and cannot be set. An agent
   * may change their own status; a supervisor may change anyone's, and only
   * a supervisor may claim `source: "supervisor"`.
   */
  setStatus: (accountId: string, userId: string, availability: Availability, source: "ui" | "supervisor" = "ui") =>
    api.patch<AgentStatus>(acct(accountId, "agents", userId, "status"), { availability, source }),
  /**
   * Every availability change, newest first, from the monthly databases.
   * Without a range it answers the current month; it pages like any other
   * view, so this one keeps crossbar's default paging rather than asking
   * for the lot.
   */
  log: (accountId: string, userId: string, range?: { from?: Date; to?: Date }, q?: ListQuery) =>
    api.list<AgentLogEntry>(acct(accountId, "agents", userId, "log"), { ...q, createdFrom: range?.from, createdTo: range?.to }),
  stats: (accountId: string, userId: string) => api.get<AgentStats>(acct(accountId, "agents", userId, "stats")),
};

/**
 * The agent's tier in each queue, from whichever shape the single read
 * carried. `tiers` is what the document says; a bare `queues` list names
 * the queues without a level or a position, which the screens then take
 * from the queue's own tier rows.
 */
export function tiersOf(agent: AgentDetail | undefined): AgentTier[] {
  if (!agent) return [];
  const tiers: AgentTier[] = [];
  for (const entry of [...(agent.queues ?? []), ...(agent.tiers ?? [])]) {
    if (typeof entry === "string") tiers.push({ queue_id: entry });
    else if (entry && typeof entry.queue_id === "string") tiers.push(entry);
  }
  return tiers;
}

/** The ids of the queues an agent has a tier in, whichever shape the read used. */
export function queueIdsOf(agent: AgentSummary | AgentDetail | undefined): string[] {
  return tiersOf(agent as AgentDetail | undefined).map((t) => t.queue_id);
}

/** "First Last", else the username, else the id. */
export const agentName = (a: Pick<AgentSummary, "first_name" | "last_name" | "username" | "id">): string =>
  `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || a.username || a.id;
